/**
 * Copyright (c) 2014-present, Facebook, Inc.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree. An additional grant
 * of patent rights can be found in the PATENTS file in the same directory.
 *
 * @flow
 */

"use strict";

type LoadObjectOperation =
    | "NONE"
    | "CREATING"
    | "LOADING"
    | "UPDATING"
    | "DELETING"
    ;

/**
 * A secret key that is used to prevent direct construction of these objects,
 * this is effectively used to ensure that the constructor is private.
 */
const SECRET = "SECRET_" + Math.random();

/**
 * Immutable Load Object. This is an immutable object that represents a
 * particular point in time for a request. Some examples:
 *
 * Render spinners while loading:
 *
 *   if (loadObject.isLoading()) {
 *     return <Spinner />;
 *   }
 *   return <div>...</div>;
 *
 * Render errors with an error:
 *
 *   if (loadObject.hasError()) {
 *     return <ErrorBox error={loadObject.getError()} />;
 *   }
 *   return <div>...</div>;
 *
 * Render normally when there's a value:
 *
 *   return <div>{loadObject.getValue().text}</div>;
 *
 */
class LoadObject<TValue> {
    private _operation: LoadObjectOperation;
    private _value: TValue | undefined;
    private _error: Error | undefined;
    private _hasValue: boolean;

    /**
     * Private construtor, never call this outside of this class.
     */
    constructor(
        secret: string,
        operation: LoadObjectOperation,
        value: TValue | undefined,
        error: Error | undefined,
        hasValue: boolean,
    )
    {
        if (secret !== SECRET)
        {
            throw new Error(
                "Construct LoadObjects using static methods such as " +
                "LoadObject.loading(), LoadObject.empty()",
            );
        }
        this._operation = operation;
        this._value = value;
        this._error = error;
        this._hasValue = hasValue;
    }

    // Convenient getters

    public getOperation(): LoadObjectOperation
    {
        return this._operation;
    }

    public getValue(): TValue | undefined
    {
        return this._value;
    }

    public getValueEnforcing(): TValue
    {
        if (!this.hasValue() || !this._value)
        {
            throw new Error("Expected load object to have a value set.");
        }

        // We check hasValue and cast rather than checking if value is null so that
        // it's possible to have "null" values that are set.
        return this._value;
    }

    public getError(): Error | undefined
    {
        return this._error;
    }

    public getErrorEnforcing(): Error
    {
        if (!this._error)
        {
            throw new Error("Expected load object to have an error set.");
        }
        return this._error;
    }

    public hasOperation(): boolean
    {
        return this._operation !== "NONE";
    }

    public hasValue(): boolean
    {
        return this._hasValue;
    }

    public hasError(): boolean
    {
        return !!this._error;
    }

    public isEmpty(): boolean
    {
        return !this.hasValue() && !this.hasOperation() && !this.hasError();
    }

    // Convenient setters

    public setOperation(operation: LoadObjectOperation): LoadObject<TValue>
    {
        if (this._operation === operation)
        {
            return this;
        }
        return new LoadObject(
            SECRET,
            operation,
            this._value,
            this._error,
            this._hasValue,
        );
    }

    public setValue(value: TValue): LoadObject<TValue>
    {
        if (this._value === value && this._hasValue === true)
        {
            return this;
        }
        return new LoadObject(
            SECRET,
            this._operation,
            value,
            this._error,
            true,
        );
    }

    public setError(error: Error): LoadObject<TValue>
    {
        if (this._error === error)
        {
            return this;
        }
        return new LoadObject(
            SECRET,
            this._operation,
            this._value,
            error,
            this._hasValue,
        );
    }

    public removeOperation(): LoadObject<TValue>
    {
        if (this._operation === "NONE")
        {
            return this;
        }
        return new LoadObject(
            SECRET,
            "NONE",
            this._value,
            this._error,
            this._hasValue,
        );
    }

    public removeValue(): LoadObject<TValue>
    {
        if (this._value === undefined && this._hasValue === false)
        {
            return this;
        }

        return new LoadObject<TValue>(
            SECRET,
            this._operation,
            undefined,
            this._error,
            false,
        );
    }

    public removeError(): LoadObject<TValue>
    {
        if (this._error === undefined)
        {
            return this;
        }
        return new LoadObject(
            SECRET,
            this._operation,
            this._value,
            undefined,
            this._hasValue,
        );
    }

    public map<TNewValue>(fn: (value: TValue) => TNewValue): LoadObject<TNewValue>
    {
        if (this.hasError())
        {
            return LoadObject.withError<TNewValue>(this.getErrorEnforcing());
        }
        if (!this.hasValue())
        {
            return LoadObject.loading();
        }

        return LoadObject.withValue(fn(this.getValueEnforcing()));
    }

    // Provide some helper methods to check specific operations

    public isDone(): boolean
    {
        return !this.hasOperation();
    }

    public isCreating(): boolean
    {
        return this.getOperation() === "CREATING";
    }

    public isLoading(): boolean
    {
        return this.getOperation() === "LOADING";
    }

    public isUpdating(): boolean
    {
        return this.getOperation() === "UPDATING";
    }

    public isDeleting(): boolean
    {
        return this.getOperation() === "DELETING";
    }

    // Provide some helpers for mutating the operations

    public done(): LoadObject<TValue>
    {
        return this.removeOperation();
    }

    public creating(): LoadObject<TValue>
    {
        return this.setOperation("CREATING");
    }

    public loading(): LoadObject<TValue>
    {
        return this.setOperation("LOADING");
    }

    public updating(): LoadObject<TValue>
    {
        return this.setOperation("UPDATING");
    }

    public deleting(): LoadObject<TValue>
    {
        return this.setOperation("DELETING");
    }

    // Static helpers for creating LoadObjects

    public static empty<TValue>(): LoadObject<TValue>
    {
        return new LoadObject<TValue>(
            SECRET,
            "NONE",
            undefined,
            undefined,
            false,
        );
    }

    public static creating<TValue>(): LoadObject<TValue>
    {
        return new LoadObject<TValue>(
            SECRET,
            "CREATING",
            undefined,
            undefined,
            false,
        );
    }

    public static loading<TValue>(): LoadObject<TValue>
    {
        return new LoadObject<TValue>(
            SECRET,
            "LOADING",
            undefined,
            undefined,
            false,
        );
    }

    public static updating<TValue>(): LoadObject<TValue>
    {
        return new LoadObject<TValue>(
            SECRET,
            "UPDATING",
            undefined,
            undefined,
            false,
        );
    }

    public static deleting<TValue>(): LoadObject<TValue>
    {
        return new LoadObject<TValue>(
            SECRET,
            "DELETING",
            undefined,
            undefined,
            false,
        );
    }

    public static withError<TValue>(error: Error): LoadObject<TValue>
    {
        return new LoadObject<TValue>(
            SECRET,
            "NONE",
            undefined,
            error,
            false,
        );
    }

    public static withValue<TValue>(value: TValue): LoadObject<TValue>
    {
        return new LoadObject(
            SECRET,
            "NONE",
            value,
            undefined,
            true,
        );
    }
}

export default LoadObject;
